import React, { useState, useEffect, useCallback } from 'react';
import { Form, InputGroup, Button, ListGroup, Spinner, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faUserPlus,
  faEnvelope,
  faUser,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { searchUsersForInvite, sendCommunityInvite } from '../../services/communityService';
import { useAuth } from '../../contexts/AuthContext';

function UserSearchInvite({ communityId, onInviteSent, onClose }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [invitingUserId, setInvitingUserId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const { refreshCsrfToken } = useAuth();

  // Debounced search
  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (term.length < 2) {
        setSearchResults([]);
        setSearching(false);
        return;
      }

      try {
        const results = await searchUsersForInvite(communityId, term);
        setSearchResults(results);
      } catch (err) {
        console.error('Search error:', err);
        setError('Napaka pri iskanju uporabnikov');
      } finally {
        setSearching(false);
      }
    }, 300),
    [communityId]
  );

  useEffect(() => {
    if (searchTerm.length >= 2) {
      setSearching(true);
      setError('');
      debouncedSearch(searchTerm);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, debouncedSearch]);

  const handleInviteUser = async (user) => {
    setInvitingUserId(user.id);
    setError('');
    setSuccess('');

    try {
      await refreshCsrfToken();
      await sendCommunityInvite(communityId, {
        userId: user.id
      });

      setSuccess(`Povabilo poslano uporabniku ${user.display_name}`);

      // Update the search results to mark this user
      setSearchResults(prev =>
        prev.map(u => u.id === user.id ? { ...u, invited: true } : u)
      );

      if (onInviteSent) {
        onInviteSent(user);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Napaka pri pošiljanju povabila';
      setError(errorMsg);
    } finally {
      setInvitingUserId(null);
    }
  };

  const handleEmailInvite = async (e) => {
    e.preventDefault();

    if (!emailInput || !validateEmail(emailInput)) {
      setError('Vnesite veljaven e-poštni naslov');
      return;
    }

    setInvitingUserId('email');
    setError('');
    setSuccess('');

    try {
      await refreshCsrfToken();
      const response = await sendCommunityInvite(communityId, {
        email: emailInput
      });

      const successMsg = response.is_new_user
        ? `Povabilo za registracijo poslano na ${emailInput}`
        : `Povabilo poslano na ${emailInput}`;

      setSuccess(successMsg);
      setEmailInput('');

      if (onInviteSent) {
        onInviteSent({ email: emailInput, isNewUser: response.is_new_user });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Napaka pri pošiljanju povabila';
      setError(errorMsg);
    } finally {
      setInvitingUserId(null);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) return null;
    // Handle relative paths
    if (profileImage.startsWith('/')) {
      return profileImage;
    }
    return profileImage;
  };

  return (
    <div className="user-search-invite">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search Input */}
      <InputGroup className="mb-3">
        <InputGroup.Text>
          <FontAwesomeIcon icon={faSearch} />
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Iskanje po imenu ali e-pošti..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
        {searching && (
          <InputGroup.Text>
            <Spinner animation="border" size="sm" />
          </InputGroup.Text>
        )}
      </InputGroup>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <ListGroup className="mb-3 search-results-list">
          {searchResults.map(user => (
            <ListGroup.Item
              key={user.id}
              className="d-flex align-items-center justify-content-between"
            >
              <div className="d-flex align-items-center">
                {user.profile_image ? (
                  <img
                    src={getProfileImageUrl(user.profile_image)}
                    alt={user.display_name}
                    className="rounded-circle me-3"
                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={`rounded-circle me-3 bg-secondary d-flex align-items-center justify-content-center text-white ${user.profile_image ? 'd-none' : ''}`}
                  style={{ width: '40px', height: '40px' }}
                >
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div>
                  <div className="fw-medium">{user.display_name}</div>
                  {user.is_member && (
                    <Badge bg="secondary" className="mt-1">
                      Ze clan
                    </Badge>
                  )}
                </div>
              </div>

              <div>
                {user.is_member ? (
                  <Badge bg="success">
                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                    Clan
                  </Badge>
                ) : user.invited ? (
                  <Badge bg="info">
                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                    Povabljen
                  </Badge>
                ) : (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleInviteUser(user)}
                    disabled={invitingUserId === user.id}
                  >
                    {invitingUserId === user.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                        Povabi
                      </>
                    )}
                  </Button>
                )}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {/* No results message */}
      {searchTerm.length >= 2 && !searching && searchResults.length === 0 && (
        <Alert variant="info" className="mb-3">
          Ni najdenih uporabnikov. Lahko povabite novega uporabnika po e-pošti.
        </Alert>
      )}

      {/* Email Fallback Option */}
      <div className="border-top pt-3 mt-3">
        <Button
          variant="link"
          className="p-0 text-decoration-none"
          onClick={() => setShowEmailFallback(!showEmailFallback)}
        >
          <FontAwesomeIcon icon={faEnvelope} className="me-2" />
          {showEmailFallback ? 'Skrij' : 'Povabi po e-pošti'}
        </Button>

        {showEmailFallback && (
          <Form onSubmit={handleEmailInvite} className="mt-3">
            <Form.Group className="mb-3">
              <Form.Label>E-poštni naslov</Form.Label>
              <Form.Control
                type="email"
                placeholder="uporabnik@primer.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
              <Form.Text className="text-muted">
                Vnesite e-poštni naslov za povabilo novega ali obstojecega uporabnika.
              </Form.Text>
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              disabled={invitingUserId === 'email'}
            >
              {invitingUserId === 'email' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Posiljanje...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                  Pošlji povabilo
                </>
              )}
            </Button>
          </Form>
        )}
      </div>

      {/* Close Button */}
      {onClose && (
        <div className="d-flex justify-content-end mt-3 pt-3 border-top">
          <Button variant="secondary" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Zapri
          </Button>
        </div>
      )}
    </div>
  );
}

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default UserSearchInvite;
